import { FABRIC_HEADERS } from '@/utilities/constants/metadata';
import { AggregationData } from '../types';
import { escapeGameUid } from '@/utilities/helpers/escapeGameUid';
import { getEndpoint } from '@/utilities/getters/gameContextGetters';
import { APIServiceType } from '../../types';

import { ErrorType } from '@/utilities/errors/types';
import { CustomError } from '@/utilities/errors/CustomError';
import { useStore } from '@/store/useStore';
import {
  buildFabricAuthHeaders,
  createApiAxios,
  getFabricAuthTokens,
} from '@/utilities/axios/api-axios';
import { processFetchDurationVital } from '@/utilities/logger/datadog/vitals';
import { VitalDurationType } from '@fanduel/casino-datadog-logger/src/types';

const fetchAggregationData = async (
  gameUid: string,
  callback?: (data: AggregationData, gameUid: string) => void,
): Promise<AggregationData> => {
  const { baseURL, region, brand, platform, product } =
    useStore.getState().runtime;
  const endpoint = getEndpoint(APIServiceType.AGGREGATION);

  if (!product || !platform || !region) {
    throw new Error('[GW][FETCH_AGGREGATION_DATA] Missing required parameters');
  }

  const vitalsGeneration = processFetchDurationVital(VitalDurationType.START);
  const escapedGameUid = escapeGameUid(gameUid);

  try {
    const instance = createApiAxios();
    const tokens = await getFabricAuthTokens('FETCH_AGGREGATION_DATA');

    const response = await instance.get<AggregationData>(endpoint, {
      baseURL,
      params: {
        product: product,
        platform: 'desktop',
        data: 'game-window',
        gameId: escapedGameUid,
        isGameUid: !!escapedGameUid,
      },
      headers: {
        ...buildFabricAuthHeaders(tokens),
        [FABRIC_HEADERS.X_BRAND]: brand,
        [FABRIC_HEADERS.X_REGION_CODE]: region,
      },
    });

    if (!response) {
      throw new Error('[GW][FETCH_AGGREGATION_DATA] No response data received');
    }

    callback?.(response.data, gameUid);

    return response.data;
  } catch (error) {
    throw new CustomError(
      error instanceof Error && error?.message.includes('[GW]')
        ? error.message
        : '[GW][FETCH_AGGREGATION_DATA] Failed',
      {
        type: ErrorType.API,
        service: APIServiceType.AGGREGATION,
        stack: error instanceof Error ? error.stack : undefined,
      },
    );
  } finally {
    processFetchDurationVital(VitalDurationType.STOP, vitalsGeneration);
  }
};

export default fetchAggregationData;
